import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { CalendarDaysIcon } from '@heroicons/react/24/outline'
import { subscriptionsApi } from '../../services/api'
import { saveSelectedCheckoutPlan } from '../../utils/checkoutPlan'
import { RootState } from '../../store/store'
import { evaluatePurchaseAccess } from '../../utils/accessFlow'
// import RichTextContent from '../../components/common/RichTextContent'

import { SharedPlanCard, Plan, BillingPeriod, BillingToggle } from '../../components/subscription/SharedPlanCard'

const BILLING_ORDER: BillingPeriod[] = ['monthly', 'quarterly', 'yearly']

interface CurrentSub {
  id: number
  plan_name: string
  status: string
  start_date: string
  end_date: string
  days_remaining: number
  auto_renew: boolean
  amount_paid: number
}

declare global {
  interface Window { Razorpay: any }
}



// Which plan gets the "Most Popular" badge — solely the one the admin marked
// is_popular. The backend enforces at most one across all plans, so at most one
// badge shows. No plan is marked → no badge (fully admin-controlled).
function resolvePopularId(plans: Plan[]): number | null {
  const flagged = plans.find(p => p.is_popular)
  return flagged ? flagged.id : null
}



export default function SubscriptionPage() {
  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)
  const [plans, setPlans] = useState<Plan[]>([])
  const [currentSub, setCurrentSub] = useState<CurrentSub | null>(null)
  const [loading, setLoading] = useState(true)
  const [billing, setBilling] = useState<BillingPeriod>('monthly')

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const [plansRes] = await Promise.all([subscriptionsApi.getPlans()])
        setPlans(plansRes)
      } catch {}
      try {
        const sub = await subscriptionsApi.getCurrentSubscription()
        setCurrentSub(sub)
      } catch {}
      setLoading(false)
    }
    load()
  }, [])

  const handleSubscribe = async (plan: Plan) => {
    saveSelectedCheckoutPlan(plan.slug)
    const next = `/dashboard/checkout?planSlug=${plan.slug}`
    const access = await evaluatePurchaseAccess(user, { next })
    navigate(access.allowed ? next : (access.redirectTo || '/dashboard/subscription'))
  }

  const availablePeriods = useMemo(
    () => BILLING_ORDER.filter(period => plans.some(plan => (plan.billing_period || 'monthly') === period)),
    [plans]
  )

  useEffect(() => {
    if (availablePeriods.length > 0 && !availablePeriods.includes(billing)) {
      setBilling(availablePeriods[0])
    }
  }, [availablePeriods, billing])

  const visiblePlans = useMemo(() => {
    return plans.filter(plan => (plan.billing_period || 'monthly') === billing)
  }, [plans, billing])



  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-8 max-w-5xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Subscription Plans</h1>
        <p className="text-sm text-gray-400 mt-1">
          SEBI registered advisory service — fixed fee, no execution, no conflict of interest.
        </p>
      </div>

      {currentSub ? (
        /* ── Has an active plan: show only the active subscription ── */
        <div className="bg-dark-800 border border-primary-500/30 rounded-2xl p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="w-2 h-2 rounded-full bg-green-400 inline-block" />
                <span className="text-xs font-semibold text-green-400 uppercase tracking-wider">Active Plan</span>
              </div>
              <h2 className="text-xl font-bold text-white">{currentSub.plan_name}</h2>
              <p className="text-sm text-gray-400 mt-1">
                ₹{Number(currentSub.amount_paid).toLocaleString('en-IN')} paid
              </p>
            </div>
            <div className="flex gap-6 text-center">
              <div>
                <div className="flex items-center gap-1 text-gray-400 text-xs mb-0.5">
                  <CalendarDaysIcon className="w-3.5 h-3.5" />
                  Expires
                </div>
                <p className="text-white font-semibold text-sm">
                  {new Date(currentSub.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-0.5">Days left</p>
                <p className={`font-bold text-lg ${currentSub.days_remaining <= 7 ? 'text-amber-400' : 'text-white'}`}>
                  {currentSub.days_remaining}
                </p>
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            Your subscription is active. You’ll be able to renew or upgrade when it’s close to expiry.
          </p>
        </div>
      ) : (
        /* ── No active plan: prompt to buy and suggest plans ── */
        <>
          <div className="bg-primary-900/20 border border-primary-500/30 rounded-2xl p-6 text-center">
            <h2 className="text-lg font-bold text-white">You don’t have an active plan yet</h2>
            <p className="text-sm text-gray-400 mt-1">
              Subscribe to a plan below to unlock research recommendations and alerts. Pick the one that fits you.
            </p>
          </div>

          {visiblePlans.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <p>No plans available yet. Check back soon.</p>
            </div>
          ) : (
            <>
              {availablePeriods.length > 1 && (
                <div className="flex justify-center mb-8">
                  <BillingToggle value={billing} periods={availablePeriods} onChange={setBilling} />
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 items-stretch">
                {(() => { const popularId = resolvePopularId(visiblePlans); return visiblePlans.map(plan => {
                  return (
                    <SharedPlanCard
                      key={plan.id}
                      plan={plan}
                      popular={plan.id === popularId}
                      isCurrentPlan={false}
                      onGetStarted={handleSubscribe}
                    />
                  )
                }) })()}
              </div>
            </>
          )}
        </>
      )}

      {/* SEBI disclaimer */}
      <div className="border-t border-dark-700 pt-6">
        <p className="text-xs text-gray-500 leading-relaxed">
          <strong className="text-gray-400">Regulatory note:</strong> AnalyzerView (SEBI Reg. INH000023719) charges a fixed advisory fee.
          Subscription fees are non-refundable once services are delivered. All recommendations are for informational purposes only and
          do not constitute investment advice. Past performance is not indicative of future returns.
        </p>
      </div>
    </div>
  )
}
